// Constants
const SELECT_ALL = '_SELECT_ALL';
export const getOptionValue = (option) => {
    if (option?._inputValue) {
        return option._inputValue;
    }
    return option;
};
export const getCheckboxValue = (option, valueName = 'value') => {
    // Enhanced type safety: validate that valueName exists and has a valid value
    if (option && typeof option === 'object' && valueName in option) {
        const value = option[valueName];
        // Return the property value if it's not null/undefined, otherwise fallback to option
        return value ?? option;
    }
    // Preserve undefined values for backward compatibility, fallback to null for other nullish values
    return option === undefined ? undefined : option ?? null;
};
// Utility functions for object comparison and unique key generation
const isObject = (value) => value !== null && typeof value === 'object' && !Array.isArray(value);
// Performance optimization: Cache for sanitized keys to avoid repeated string operations
const sanitizeCache = new Map();
// Safe string sanitization for key generation to prevent conflicts
const sanitizeKeyPart = (value) => {
    // Use cache for performance optimization on repeated values
    if (sanitizeCache.has(value)) {
        const cachedValue = sanitizeCache.get(value);
        return cachedValue;
    }
    const str = String(value);
    // Replace potentially conflicting characters with safe alternatives
    // This prevents ambiguity in key parsing while maintaining readability
    const sanitized = str.replace(/[-_]/g, (char) => char === '-' ? '·' : '‧');
    // Cache the result (limit cache size to prevent memory leaks)
    // istanbul ignore else
    if (sanitizeCache.size < 1000) {
        sanitizeCache.set(value, sanitized);
    }
    return sanitized;
};
export const isEqualOptions = (a, b, visited = new WeakSet()) => {
    // Reference equality check first (most common case)
    if (a === b)
        return true;
    // Enhanced boundary conditions: Handle null/undefined cases more explicitly
    if (a == null || b == null)
        return a === b;
    // Handle empty arrays specifically
    if (Array.isArray(a) && Array.isArray(b)) {
        if (a.length === 0 && b.length === 0)
            return true;
        if (a.length === 0 || b.length === 0)
            return false;
    }
    // Handle different types
    if (typeof a !== typeof b)
        return false;
    // For primitive types
    if (typeof a !== 'object')
        return a === b;
    // Circular reference protection - check if we've seen these objects before
    if (visited.has(a) || visited.has(b)) {
        // If we've encountered either object before, use reference equality
        return a === b;
    }
    // Add objects to visited set to track them
    visited.add(a);
    visited.add(b);
    // For objects, compare key properties
    if (isObject(a) && isObject(b)) {
        // Check common option properties - be precise about undefined vs null
        const aHasValue = 'value' in a;
        const bHasValue = 'value' in b;
        const aHasId = 'id' in a;
        const bHasId = 'id' in b;
        const aHasKey = 'key' in a;
        const bHasKey = 'key' in b;
        // If both have value property, compare it (even if null/undefined)
        if (aHasValue && bHasValue) {
            return a.value === b.value;
        }
        // If both have id property, compare it
        if (aHasId && bHasId) {
            return a.id === b.id;
        }
        // If both have key property, compare it
        if (aHasKey && bHasKey) {
            return a.key === b.key;
        }
        // Use fallback logic for non-null/undefined values only
        const getValueFallback = (obj) => {
            if (obj.value !== undefined && obj.value !== null)
                return obj.value;
            if (obj.id !== undefined && obj.id !== null)
                return obj.id;
            if (obj.key !== undefined && obj.key !== null)
                return obj.key;
            return undefined;
        };
        const aValue = getValueFallback(a);
        const bValue = getValueFallback(b);
        if (aValue !== undefined && bValue !== undefined) {
            return aValue === bValue;
        }
        // Fallback to label comparison
        const getLabelFallback = (obj) => {
            if (obj.label !== undefined && obj.label !== null)
                return obj.label;
            if (obj.name !== undefined && obj.name !== null)
                return obj.name;
            if (obj.text !== undefined && obj.text !== null)
                return obj.text;
            return undefined;
        };
        const aLabel = getLabelFallback(a);
        const bLabel = getLabelFallback(b);
        if (aLabel !== undefined && bLabel !== undefined) {
            return aLabel === bLabel;
        }
        // If no standard properties, compare all enumerable properties
        const aKeys = Object.keys(a);
        const bKeys = Object.keys(b);
        if (aKeys.length !== bKeys.length)
            return false;
        return aKeys.every((key) => {
            if (!bKeys.includes(key))
                return false;
            return isEqualOptions(a[key], b[key], visited);
        });
    }
    // For arrays
    if (Array.isArray(a) && Array.isArray(b)) {
        if (a.length !== b.length)
            return false;
        return a.every((item, index) => isEqualOptions(item, b[index], visited));
    }
    return false;
};
export const generateUniqueKey = (opt, index, prefix = 'option') => {
    // Enhanced boundary conditions: Handle null/undefined options
    if (opt == null) {
        return `${prefix}-null-${index}`;
    }
    // Handle primitive values
    if (typeof opt !== 'object') {
        return `${prefix}-primitive-${sanitizeKeyPart(opt)}-${index}`;
    }
    // Handle creatable options with enhanced uniqueness
    if (opt._inputValue !== undefined && opt._inputValue !== null) {
        // Include index to ensure uniqueness even with same input values
        // This prevents key conflicts when users create multiple items with same name
        return `create-${sanitizeKeyPart(opt._inputValue)}-${index}`;
    }
    if (opt[SELECT_ALL])
        return 'select-all';
    // Try common unique identifiers in order of preference
    if (opt?.id !== undefined && opt?.id !== null) {
        return `${prefix}-id-${sanitizeKeyPart(opt.id)}`;
    }
    if (opt?.key !== undefined && opt?.key !== null) {
        return `${prefix}-key-${sanitizeKeyPart(opt.key)}`;
    }
    if (opt?.value !== undefined && opt?.value !== null) {
        const valueStr = typeof opt.value === 'object'
            ? `obj-${sanitizeKeyPart(opt.value?.id ?? opt.value?.key ?? opt.value?.value ?? index)}`
            : `val-${sanitizeKeyPart(opt.value)}`;
        return `${prefix}-${valueStr}`;
    }
    // For objects with label but no unique id/key/value, combine label with index
    if (opt?.label !== undefined && opt?.label !== null) {
        return `${prefix}-label-${sanitizeKeyPart(opt.label)}-${index}`;
    }
    if (opt?.name !== undefined && opt?.name !== null) {
        return `${prefix}-name-${sanitizeKeyPart(opt.name)}-${index}`;
    }
    if (opt?.text !== undefined && opt?.text !== null) {
        return `${prefix}-text-${sanitizeKeyPart(opt.text)}-${index}`;
    }
    // Last resort: ensure uniqueness with index
    return `${prefix}-${index}`;
};
